import { Precision } from "@framework/provider-asu";

import { IResponseTrain, IResponsePoint, JournalRecorderVisualItem } from "./interfaces";

const journalRecorderItemTimeMs = (rawDate: string, _dbTz: number): number => new Date(rawDate).getTime();

const journalRecorderBucketEndMs = (startMs: number, precision: Precision): number => {
  const end = new Date(startMs);
  if (precision === Precision.month) {
    end.setUTCMonth(end.getUTCMonth() + 1);
  } else if (precision === Precision.day) {
    end.setUTCDate(end.getUTCDate() + 1);
  } else {
    end.setUTCHours(end.getUTCHours() + 1);
  }
  return end.getTime();
};

const isJournalRecorderBucketInWindow = (
  rawDate: string,
  windowStartIso: string,
  windowEndIso: string,
  dbTz: number,
  precision: Precision,
): boolean => {
  const left = new Date(windowStartIso).getTime();
  const right = new Date(windowEndIso).getTime();
  if (Number.isNaN(left) || Number.isNaN(right)) return true;

  const bucketStart = journalRecorderItemTimeMs(rawDate, dbTz);
  if (Number.isNaN(bucketStart)) return false;

  const bucketEnd = journalRecorderBucketEndMs(bucketStart, precision);
  return bucketStart <= right && bucketEnd > left;
};

export const mergeRecorderTrains = (
  prev: IResponseTrain[],
  next: IResponseTrain[],
  fullReplace: boolean,
): IResponseTrain[] => {
  if (fullReplace || !prev.length) return next.slice();
  const map = new Map<string, IResponseTrain>();
  for (const row of prev) map.set(`${row.idTrain}\u0001${row.Date}`, row);
  for (const row of next) {
    const key = `${row.idTrain}\u0001${row.Date}`;
    const existing = map.get(key);
    map.set(
      key,
      existing
        ? {
            ...existing,
            AlarmStatus: Math.max(existing.AlarmStatus, row.AlarmStatus),
            Count: existing.Count + row.Count,
          }
        : row,
    );
  }
  return [...map.values()];
};

export const mergeRecorderPoints = (
  prev: IResponsePoint[],
  next: IResponsePoint[],
  fullReplace: boolean,
): IResponsePoint[] => {
  if (fullReplace || !prev.length) return next.slice();
  const map = new Map<string, IResponsePoint>();
  for (const row of prev) map.set(`${row.idPoint}\u0001${row.Date}`, row);
  for (const row of next) {
    const key = `${row.idPoint}\u0001${row.Date}`;
    const existing = map.get(key);
    map.set(
      key,
      existing
        ? {
            ...existing,
            AlarmStatus: Math.max(existing.AlarmStatus, row.AlarmStatus),
            Count: existing.Count + row.Count,
          }
        : row,
    );
  }
  return [...map.values()];
};

const recorderVisualKey = (item: JournalRecorderVisualItem): string => {
  if ("trainId" in item && "controlDate" in item) {
    return `t_${item.trainId}\u0001${item.controlDate}`;
  }
  return `p_${item.paramId}\u0001${item.date}`;
};

export const mergeRecorderVisual = (
  prev: JournalRecorderVisualItem[],
  next: JournalRecorderVisualItem[],
  fullReplace: boolean,
): JournalRecorderVisualItem[] => {
  if (fullReplace || !prev.length) return next.slice();
  const map = new Map<string, JournalRecorderVisualItem>();
  for (const row of prev) map.set(recorderVisualKey(row), row);
  for (const row of next) map.set(recorderVisualKey(row), row);
  return [...map.values()];
};

export const filterJournalTrainsToWindow = (
  rows: IResponseTrain[],
  windowStartIso: string,
  windowEndIso: string,
  dbTz: number,
  precision: Precision,
): IResponseTrain[] => {
  return rows.filter(r => isJournalRecorderBucketInWindow(r.Date, windowStartIso, windowEndIso, dbTz, precision));
};

export const filterJournalPointsToWindow = (
  rows: IResponsePoint[],
  windowStartIso: string,
  windowEndIso: string,
  dbTz: number,
  precision: Precision,
): IResponsePoint[] => {
  return rows.filter(r => isJournalRecorderBucketInWindow(r.Date, windowStartIso, windowEndIso, dbTz, precision));
};

export const filterJournalVisualToWindow = (
  rows: JournalRecorderVisualItem[],
  windowStartIso: string,
  windowEndIso: string,
  dbTz: number,
  precision: Precision,
): JournalRecorderVisualItem[] => {
  return rows.filter(item => {
    const raw = "controlDate" in item ? item.controlDate : item.date;
    return isJournalRecorderBucketInWindow(raw, windowStartIso, windowEndIso, dbTz, precision);
  });
};

export const replaceRecorderVisualInWindow = (
  previous: JournalRecorderVisualItem[],
  next: JournalRecorderVisualItem[],
  windowStartIso: string,
  windowEndIso: string,
  dbTz: number,
  precision: Precision,
): JournalRecorderVisualItem[] => {
  const previousInWindow = new Set(
    filterJournalVisualToWindow(previous, windowStartIso, windowEndIso, dbTz, precision),
  );
  const previousOutsideWindow = previous.filter(item => !previousInWindow.has(item));

  return mergeRecorderVisual(previousOutsideWindow, next, false);
};

export const applyRecorderVisualSnapshot = (
  previous: JournalRecorderVisualItem[],
  snapshot: JournalRecorderVisualItem[],
  pendingDeltas: JournalRecorderVisualItem[],
  windowStartIso: string,
  windowEndIso: string,
  dbTz: number,
  precision: Precision,
): JournalRecorderVisualItem[] =>
  mergeRecorderVisual(
    replaceRecorderVisualInWindow(previous, snapshot, windowStartIso, windowEndIso, dbTz, precision),
    pendingDeltas,
    false,
  );
